import { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useGlobal } from 'reactn';
import { get, flatten } from 'lodash';
import Card from '../../common/components/Card';
import DateRangePicker from '../../common/components/DateRangePicker';
import Button from '../../common/components/Button';
import DataGrid from '../../common/components/DataGrid';
import TextLoader from '../../common/components/TextLoader';
import { GET_TIMESHEET_WEEKLY_SNAPSHOT } from '../queries';
import { fetchTimesheetsForPeriod } from '../index';
import { calculateCost } from '../../project-manager/components/ProjectFinancialsDetailsTable';
import { Modal, TextInput, ActionIcon, Select, Table } from '@mantine/core';
import { AgGridColumn } from '@ag-grid-community/react';
import client from '../../../api/graphql/client';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { modals } from '@mantine/modals';
import React from 'react';
import { hasTokens, replaceTokens } from '../utils/tokenProcessor';
import { extractAndEnrichUserData, getUserDataForRow, getAvailableTokenFields } from '../utils/userDataExtractor';
import TokenSelector from './TokenSelector';

const formatDate = dateStr => {
  let dateString = dateStr;
  let year = dateString.substring(0, 4);
  let month = dateString.substring(4, 6);
  let day = dateString.substring(6, 8);
  let date = new Date(year, month - 1, day);
  return date.toLocaleDateString('fr-CA');
};

const createGridData = timesheets =>
  flatten(
    timesheets.map(ts =>
      ts.lineItems.map(li => {
        let bu = `${get(li, 'project.accountingCode') || ''}-${
          get(li, 'project.accountingCodeSuffix') || ''
        }`;
        if (get(li, 'deliverable.accountingCode') && get(li, 'deliverable.accountingCodeSuffix')) {
          bu = `${get(li, 'deliverable.accountingCode') || ''}-${
            get(li, 'deliverable.accountingCodeSuffix') || ''
          }`;
        }
        let days = ['monTask', 'tueTask', 'wedTask', 'thuTask', 'friTask', 'satTask', 'sunTask'];
        let finalData = [];
        for (let i = 0; i < days.length; i++) {
          const userRole = get(ts, 'user.role');
          const email = get(ts, 'user.emails[0].address');
          const projectRoles = get(li, 'project.roles') || [];
          const resources = get(li, 'project.resources') || [];
          const resourceRole = resources.find(r => r.email === email)?.role;
          const role = resourceRole || userRole;
          const defaultRate = get(ts, 'user.defaultRate');
          const status = get(ts, 'status');
          const approvers =
            status === 'Approved'
              ? get(ts, 'approvalsRequired')
                  ?.map(a => a.approvedBy)
                  ?.join(', ') || undefined
              : undefined;
          const approvedOn =
            status === 'Approved'
              ? get(ts, 'approvalsRequired')?.sort(
                  (a, b) => new Date(b.approvedOn) - new Date(a.approvedOn),
                )[0]?.approvedOn
              : undefined;

          const clientName = get(li, 'project.client.shortName');
          let temp = {
            timesheetId: get(ts, '_id'),
            status,
            endDate: moment(get(ts, 'endDate')).format('MM-DD-YYYY'),
            name: `${get(ts, 'user.firstName')} ${get(ts, 'user.lastName')}`,
            firstName: get(ts, 'user.firstName'),
            lastName: get(ts, 'user.lastName'),
            role,
            payrollId: get(ts, 'user.payrollId'),
            email: get(ts, 'user.emails[0].address'),
            clientName,
            project: get(li, 'project.name'),
            projectCode: get(li, 'project.projectCode'),
            roles: get(li, 'project.roles')?.length || 0,
            bu: bu,
            milestone: get(li, 'deliverable.milestoneName'),
            deliverable: get(li, 'deliverable.deliverableName'),
            supervisor: get(ts, 'user.supervisor.firstName')
              ? get(ts, 'user.supervisor.firstName') + ' ' + get(ts, 'user.supervisor.lastName')
              : undefined,
            approvedOn: approvedOn
              ? moment(approvedOn).format('MM-DD-YYYY')
              : status === 'Approved'
              ? moment(get(ts, 'updatedAt')).format('MM-DD-YYYY')
              : undefined,
            approvers,
            department: get(ts, 'user.department'),
            projectId: get(li, 'project._id'),
          };
          let taskdate = get(li, days[i] + '.date');
          let taskhour = get(li, days[i] + '.hours');
          let comment = get(li, days[i] + '.comment');

          if (taskdate && taskhour) {
            const totalCost = calculateCost({
              email,
              hours: taskhour,
              resources,
              roles: projectRoles,
              userRole,
              defaultRate,
            });

            temp['taskdate'] = formatDate(taskdate);
            temp['hours'] = taskhour;
            temp['comment'] = comment;
            temp['totalCost'] = totalCost;
            temp['rate'] = totalCost / taskhour;

            finalData.push(temp);
          }
        }
        return finalData;
      }),
    ),
  );

// List of all columns for use in Select components
const ALL_COLUMNS = [
  { value: 'endDate', label: 'Week Ending' },
  { value: 'status', label: 'Status' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'firstName', label: 'First Name' },
  { value: 'payrollId', label: 'Payroll ID' },
  { value: 'email', label: 'Email' },
  { value: 'clientName', label: 'Client Name' },
  { value: 'project', label: 'Project' },
  { value: 'projectCode', label: 'Project Code' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'deliverable', label: 'Deliverable' },
  { value: 'taskdate', label: 'Task Date' },
  { value: 'hours', label: 'Hours' },
  { value: 'role', label: 'Role' },
  { value: 'rate', label: 'Rate' },
  { value: 'name', label: 'Name' },
  { value: 'totalCost', label: 'Total Cost' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'approvers', label: 'Approvers' },
  { value: 'approvedOn', label: 'Approved On' },
  { value: 'comment', label: 'Comment' },
  { value: 'roles', label: 'Roles' },
  { value: 'bu', label: 'BU' },
  { value: 'department', label: 'Department' },
  { value: 'classification', label: 'Classification' },
];

const UPDATE_USER_SETTINGS = gql`
  mutation UpdateUserSettings($id: ID!, $input: UpdateUserSettingsInput!) {
    updateUserSettings(_id: $id, input: $input)
  }
`;

const GET_MY_SETTINGS = gql`
  query GET_MY_SETTINGS {
    me {
      _id
      settings {
        timesheetDailySnapshotOverrides
      }
    }
  }
`;

const GET_PROJECT_CLASSIFICATIONS = gql`
  query GetProjectClassifications($projectIds: [String!]!) {
    finance {
      projects(projectIds: $projectIds) {
        _id
        classification
      }
    }
  }
`;

function OverrideRuleEditor({ initialConditions, initialOverrideFields, onSave, availableTokenFields = [] }) {
  const [conditions, setConditions] = useState(initialConditions);
  const [overrideFields, setOverrideFields] = useState(initialOverrideFields);
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 border-b mb-2">
        <div className="font-semibold text-lg">Conditions</div>
        <div className="flex flex-col gap-2">
          {conditions.map((cond, idx) => (
            <div
              key={idx}
              className="flex flex-row items-end bg-gray-50 rounded border relative gap-2"
            >
              <div className="flex flex-col w-1/2">
                <Select
                  placeholder="Select field"
                  data={ALL_COLUMNS}
                  value={cond.field}
                  onChange={field => {
                    const newConds = [...conditions];
                    newConds[idx].field = field;
                    setConditions(newConds);
                  }}
                  searchable
                  clearable
                />
              </div>
              <div className="flex flex-col w-1/2">
                <TextInput
                  placeholder="Value"
                  value={cond.value}
                  onChange={e => {
                    const newConds = [...conditions];
                    newConds[idx].value = e.target.value;
                    setConditions(newConds);
                  }}
                  disabled={!cond.field}
                />
              </div>
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => {
                  setConditions(conditions.filter((_, i) => i !== idx));
                }}
                title="Remove Condition"
                className="hover:bg-red-100 hover:text-red-700 transition h-9 w-9 flex items-center justify-center"
                style={{ minHeight: 36, minWidth: 36 }}
              >
                <span className="text-lg font-bold">×</span>
              </ActionIcon>
            </div>
          ))}
        </div>
        <Button
          size="small"
          variant="outline"
          color="blue"
          onClick={() => setConditions([...conditions, { field: '', value: '' }])}
          className="w-fit mt-2 self-center"
        >
          + Add Condition
        </Button>
      </div>
      <div className="flex flex-col md:flex-row gap-4 items-end border-b pb-6 mb-2">
        <div className="flex-1 flex flex-col gap-2">
          <div className="font-semibold text-lg">Override Fields</div>
          {overrideFields.map((ovr, idx) => (
            <div
              key={idx}
              className="flex flex-row items-end bg-gray-50 rounded border relative gap-2 mb-2"
            >
              <div className="flex flex-col w-1/2">
                <Select
                  placeholder="Select field to override"
                  data={ALL_COLUMNS}
                  value={ovr.field}
                  onChange={field => {
                    const newOvr = [...overrideFields];
                    newOvr[idx].field = field;
                    setOverrideFields(newOvr);
                  }}
                  searchable
                  clearable
                />
              </div>
              <div className="flex flex-col w-1/2">
                <TokenSelector
                  placeholder="Override value"
                  value={ovr.value}
                  onChange={value => {
                    const newOvr = [...overrideFields];
                    newOvr[idx].value = value;
                    setOverrideFields(newOvr);
                  }}
                  availableFields={availableTokenFields}
                  disabled={!ovr.field}
                />
              </div>
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => {
                  setOverrideFields(overrideFields.filter((_, i) => i !== idx));
                }}
                title="Remove Override Field"
                className="hover:bg-red-100 hover:text-red-700 transition h-9 w-1/2 flex items-center justify-center"
                style={{ minHeight: 36, minWidth: 36 }}
              >
                <span className="text-lg font-bold">×</span>
              </ActionIcon>
            </div>
          ))}
          <Button
            size="small"
            variant="outline"
            color="blue"
            onClick={() => setOverrideFields([...overrideFields, { field: '', value: '' }])}
            className="w-fit mt-2 self-center"
          >
            + Add Override Field
          </Button>
        </div>
      </div>
      <div className="flex flex-row justify-end mb-4 gap-2">
        <Button
          color="green"
          variant="filled"
          disabled={
            conditions.length === 0 ||
            overrideFields.length === 0 ||
            conditions.some(c => !c.field || !c.value) ||
            overrideFields.some(o => !o.field || o.value === undefined || o.value === '')
          }
          onClick={() => onSave(conditions, overrideFields)}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

const TimesheetDailySnapshotEnhancedReport = () => {
  const [config] = useGlobal('config');
  const dailySnapshotOverridesEnabled = config['daily-snapshot-overrides'] === 'true';
  const { data, refetch } = useQuery(GET_MY_SETTINGS);
  const userId = data?.me?._id;
  const userSettings = data?.me?.settings;
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(null);
  const [startDate, setStartDate] = useState(moment().startOf('isoWeek').format('YYYYMMDD'));
  const [endDate, setEndDate] = useState(moment().endOf('isoWeek').format('YYYYMMDD'));
  const [rowData, setRowData] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [unsavedOverrides, setUnsavedOverrides] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const moveOverride = (fromIndex, toIndex) => {
    setOverrides(prev => {
      if (toIndex < 0 || toIndex >= prev.length || fromIndex === toIndex) return prev;
      const next = [...prev];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    });
  };
  const [modalOpened, setModalOpened] = useState(false);
  const [enrichedUserMap, setEnrichedUserMap] = useState(new Map());
  const [availableTokenFields, setAvailableTokenFields] = useState([]);
  const [projectClassifications, setProjectClassifications] = useState({});
  const exportRef = useRef();
  const onExportReady = ({ getExportFunc }) => {
    exportRef.current = getExportFunc();
  };
  const handleExportClick = () => {
    if (exportRef.current) exportRef.current();
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const fetchedData = await fetchTimesheetsForPeriod({
        startDate,
        endDate,
        setLoadingText,
        query: GET_TIMESHEET_WEEKLY_SNAPSHOT,
      });
      // Remove tasks for days that fall outside of the selected range
      const timesheetsWithTrimmedTasks = fetchedData.map(timesheet => {
        const newLineItems = timesheet.lineItems.map(lineItem => {
          for (const property in lineItem) {
            if (property.endsWith('Task') && lineItem[property]) {
              const { date } = lineItem[property];
              if (date < startDate || date > endDate) {
                delete lineItem[property];
              }
            }
          }
          return lineItem;
        });
        return {
          ...timesheet,
          lineItems: newLineItems,
        };
      });
      const IndividualTaskrow = createGridData(timesheetsWithTrimmedTasks);
      const flattenedRows = IndividualTaskrow.flat();
      
      // Extract unique project IDs
      const uniqueProjectIds = [...new Set(flattenedRows.map(row => row.projectId).filter(Boolean))];
      
      // Fetch project classifications
      if (uniqueProjectIds.length > 0) {
        try {
          const { data: classificationData } = await client.query({
            query: GET_PROJECT_CLASSIFICATIONS,
            variables: { projectIds: uniqueProjectIds }
          });
          
          // Create a map of project ID to classification
          const classificationsMap = {};
          if (classificationData?.finance?.projects) {
            classificationData.finance.projects.forEach(project => {
              if (project._id && project.classification) {
                classificationsMap[project._id] = project.classification;
              }
            });
          }
          setProjectClassifications(classificationsMap);
        } catch (error) {
          console.error('Error fetching project classifications:', error);
        }
      }
      
      setRowData(flattenedRows);
      
      // Check if any overrides have tokens that need user data (only in override values)
      const hasTokensInOverrides = overrides.some(ovr => 
        Object.values(ovr.override).some(v => hasTokens(v))
      );
      
      let userMap = new Map();
      if (hasTokensInOverrides && flattenedRows.length > 0) {
        // Fetch enriched user data
        userMap = await extractAndEnrichUserData(flattenedRows);
        setEnrichedUserMap(userMap);
      }
      
      // Get available token fields
      if (flattenedRows.length > 0) {
        const tokenFields = getAvailableTokenFields(flattenedRows[0], userMap);
        setAvailableTokenFields(tokenFields);
      }
      
      setLoading(false);
    };
    fetchData().catch(console.error);
  }, [startDate, endDate, overrides]);

  // Load overrides from user settings on mount and when settings change
  useEffect(() => {
    const val = userSettings?.timesheetDailySnapshotOverrides;
    if (val && val !== '[]') {
      try {
        const parsedOverrides = JSON.parse(val);
        setOverrides(parsedOverrides);
        setUnsavedOverrides(parsedOverrides);
      } catch (e) {}
    }
  }, [userSettings]);

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(JSON.stringify(overrides) !== JSON.stringify(unsavedOverrides));
  }, [overrides, unsavedOverrides]);

  // Manual save function
  const handleSaveOverrides = async () => {
    if (!userId) return;
    try {
      await client.mutate({
        mutation: UPDATE_USER_SETTINGS,
        variables: {
          id: userId,
          input: {
            timesheetDailySnapshotOverrides: JSON.stringify(overrides),
          },
        },
      });
      await refetch();
      setUnsavedOverrides(overrides);
      setHasUnsavedChanges(false);
    } catch (e) {
      console.error('Failed to save overrides:', e);
    }
  };

  // Helper to get label from ALL_COLUMNS
  const getColumnLabel = field => {
    const col = ALL_COLUMNS.find(col => col.value === field);
    return col ? col.label : field;
  };

  return (
    <Card
      border
      title="Timesheet Daily Snapshot"
      floating
      padded={false}
      actionComponent={
        <div className="flex flex-col gap-2">
          <div className="flex items-between w-full gap-4">
            <div className="">
              {dailySnapshotOverridesEnabled && (
                <Button variant="outline" color="blue" onClick={() => setModalOpened(true)}>
                  Manage Overrides
                </Button>
              )}
            </div>
            <div className="flex items-center gap-4">
              <DateRangePicker
                loading={loading ? true : undefined}
                handleDateChange={range => {
                  setStartDate(moment(range[0]).format('YYYYMMDD'));
                  setEndDate(moment(range[1]).format('YYYYMMDD'));
                }}
                startDate={startDate}
                endDate={endDate}
              />
              <div className="">
                <Button disabled={loading} onClick={handleExportClick}>
                  Export
                </Button>
              </div>
            </div>
          </div>
          <Modal
            opened={modalOpened}
            onClose={() => {
              if (hasUnsavedChanges) {
                if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                  setOverrides(unsavedOverrides);
                  setHasUnsavedChanges(false);
                  setModalOpened(false);
                }
              } else {
                setModalOpened(false);
              }
            }}
            title={
              <div className="flex items-center gap-2">
                <span>Field Overrides</span>
                {hasUnsavedChanges && (
                  <span className="text-sm text-orange-600 font-normal">(unsaved changes)</span>
                )}
              </div>
            }
            size="xxl"
            yOffset="2vh"
            overlayProps={{ blur: 2 }}
            className="[&>div]:!p-8"
            styles={{
              content: {
                maxHeight: '90vh',
                overflowY: 'auto',
                minWidth: 'min(1100px, 98vw)',
                maxWidth: '98vw',
              },
            }}
          >
            <div className="flex justify-between mb-2 gap-2 ml-auto">
              <div className="flex flex-row w-full justify-between items-center gap-2">
                <div className="flex flex-row gap-2">
                  <Button
                    size="small"
                    variant="outline"
                    color="blue"
                    onClick={() => {
                      const data = JSON.stringify(overrides, null, 2);
                      const blob = new Blob([data], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'timesheet-daily-snapshot-overrides.json';
                      document.body.appendChild(a);
                      a.click();
                      setTimeout(() => {
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }, 0);
                    }}
                  >
                    Export Rules
                  </Button>
                  <Button
                    size="small"
                    variant="outline"
                    color="blue"
                    onClick={() => document.getElementById('import-overrides-input').click()}
                  >
                    Import Rules
                  </Button>
                  <input
                    id="import-overrides-input"
                    type="file"
                    accept="application/json"
                    style={{ display: 'none' }}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = event => {
                        try {
                          const imported = JSON.parse(event.target.result);
                          if (Array.isArray(imported)) {
                            if (
                              window.confirm(
                                'Are you sure you want to import and overwrite your current overrides?',
                              )
                            ) {
                              setOverrides(imported);
                            }
                          } else {
                            alert('Invalid format: JSON must be an array.');
                          }
                        } catch (err) {
                          alert('Failed to import: Invalid JSON.');
                        }
                      };
                      reader.readAsText(file);
                      e.target.value = '';
                    }}
                  />
                </div>
                <div className="flex flex-row gap-2 ml-auto">
                  <Button
                    color="blue"
                    variant="outline"
                    onClick={() => {
                      // Open Mantine modals manager modal for adding a new rule
                      let tempConditions = [{ field: '', value: '' }];
                      let tempOverrideFields = [{ field: '', value: '' }];
                      modals.open({
                        title: 'Add New Override Rule',
                        size: 'lg',
                        children: (
                          <OverrideRuleEditor
                            initialConditions={tempConditions}
                            initialOverrideFields={tempOverrideFields}
                            availableTokenFields={availableTokenFields}
                            onSave={(conditions, overrideFields) => {
                              const overrideObj = {};
                              overrideFields.forEach(o => {
                                if (o.field && o.value !== undefined && o.value !== '') {
                                  overrideObj[o.field] = o.value;
                                }
                              });
                              setOverrides(prev => [
                                ...prev,
                                {
                                  conditions: conditions.filter(c => c.field && c.value),
                                  override: overrideObj,
                                },
                              ]);
                              modals.closeAll();
                            }}
                          />
                        ),
                      });
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
            <div className="mt-2 relative">
              {overrides.length > 0 ? (
                <Table
                  verticalSpacing="xs"
                  horizontalSpacing="xs"
                  fontSize="xs"
                  className="min-w-full"
                  withBorder
                >
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="rounded-tl whitespace-nowrap">Conditions</th>
                      <th className="whitespace-nowrap">Overrides</th>
                      <th className="rounded-tr text-right whitespace-nowrap"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {overrides.map((ovr, ovrIdx) => (
                      <tr
                        key={`ovr-${ovrIdx}`}
                        className="bg-white rounded border hover:bg-blue-50 transition"
                        style={{ boxShadow: 'none' }}
                      >
                        <td className="text-gray-700 align-top whitespace-nowrap">
                          <div className="flex flex-col gap-0.5">
                            {ovr.conditions.map((c, condIdx) => (
                              <div key={condIdx}>
                                <span className="font-bold text-blue-900">
                                  {getColumnLabel(c.field)}:
                                </span>{' '}
                                {c.value}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="align-top whitespace-nowrap">
                          <div className="flex flex-col gap-0.5">
                            {Object.entries(ovr.override).map(([field, value], idx) => (
                              <div key={idx} className="whitespace-nowrap">
                                <span className="font-bold text-blue-900">
                                  {getColumnLabel(field)}:
                                </span>{' '}
                                <span className={hasTokens(value) ? "text-green-700 font-mono" : "text-blue-700"}>
                                  {value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="text-right align-top">
                          <div className="flex flex-row justify-end gap-1">
                            <ActionIcon
                              color="gray"
                              variant="subtle"
                              onClick={() => moveOverride(ovrIdx, ovrIdx - 1)}
                              disabled={ovrIdx === 0}
                              title="Move Up"
                              className="hover:bg-gray-100 hover:text-gray-700 transition mr-1"
                              style={{ minHeight: 28, minWidth: 28 }}
                            >
                              <span className="text-base font-bold">↑</span>
                            </ActionIcon>
                            <ActionIcon
                              color="gray"
                              variant="subtle"
                              onClick={() => moveOverride(ovrIdx, ovrIdx + 1)}
                              disabled={ovrIdx === overrides.length - 1}
                              title="Move Down"
                              className="hover:bg-gray-100 hover:text-gray-700 transition mr-1"
                              style={{ minHeight: 28, minWidth: 28 }}
                            >
                              <span className="text-base font-bold">↓</span>
                            </ActionIcon>
                            <ActionIcon
                              color="blue"
                              variant="subtle"
                              onClick={() => {
                                // Open modal to edit this rule
                                modals.open({
                                  title: 'Edit Override Rule',
                                  size: 'lg',
                                  children: (
                                    <OverrideRuleEditor
                                      initialConditions={ovr.conditions.map(c => ({ ...c }))}
                                      initialOverrideFields={Object.entries(ovr.override).map(
                                        ([field, value]) => ({ field, value }),
                                      )}
                                      availableTokenFields={availableTokenFields}
                                      onSave={(conditions, overrideFields) => {
                                        const overrideObj = {};
                                        overrideFields.forEach(o => {
                                          if (o.field && o.value !== undefined && o.value !== '') {
                                            overrideObj[o.field] = o.value;
                                          }
                                        });
                                        setOverrides(prev => [
                                          ...prev.slice(0, ovrIdx),
                                          {
                                            conditions: conditions.filter(c => c.field && c.value),
                                            override: overrideObj,
                                          },
                                          ...prev.slice(ovrIdx + 1),
                                        ]);
                                        modals.closeAll();
                                      }}
                                    />
                                  ),
                                });
                              }}
                              title="Edit Override"
                              className="hover:bg-blue-100 hover:text-blue-700 transition mr-1"
                              style={{ minHeight: 28, minWidth: 28 }}
                            >
                              <span className="text-base font-bold">✎</span>
                            </ActionIcon>
                            <ActionIcon
                              color="gray"
                              variant="subtle"
                              onClick={() => {
                                // Duplicate rule and open edit modal for the new rule
                                const newConditions = ovr.conditions.map(c => ({ ...c }));
                                const newOverrideFields = Object.entries(ovr.override).map(
                                  ([field, value]) => ({ field, value }),
                                );
                                // Insert duplicate after current
                                setOverrides(prev => {
                                  const newRule = {
                                    conditions: newConditions,
                                    override: { ...ovr.override },
                                  };
                                  const newOverrides = [
                                    ...prev.slice(0, ovrIdx + 1),
                                    newRule,
                                    ...prev.slice(ovrIdx + 1),
                                  ];
                                  // Open modal for the new rule after state update
                                  setTimeout(() => {
                                    modals.open({
                                      title: 'Edit Duplicated Override Rule',
                                      size: 'lg',
                                      children: (
                                        <OverrideRuleEditor
                                          initialConditions={newConditions.map(c => ({ ...c }))}
                                          initialOverrideFields={newOverrideFields.map(f => ({
                                            ...f,
                                          }))}
                                          availableTokenFields={availableTokenFields}
                                          onSave={(conditions, overrideFields) => {
                                            const overrideObj = {};
                                            overrideFields.forEach(o => {
                                              if (
                                                o.field &&
                                                o.value !== undefined &&
                                                o.value !== ''
                                              ) {
                                                overrideObj[o.field] = o.value;
                                              }
                                            });
                                            setOverrides(current => {
                                              const idx = ovrIdx + 1;
                                              return [
                                                ...current.slice(0, idx),
                                                {
                                                  conditions: conditions.filter(
                                                    c => c.field && c.value,
                                                  ),
                                                  override: overrideObj,
                                                },
                                                ...current.slice(idx + 1),
                                              ];
                                            });
                                            modals.closeAll();
                                          }}
                                        />
                                      ),
                                    });
                                  }, 0);
                                  return newOverrides;
                                });
                              }}
                              title="Duplicate Override"
                              className="hover:bg-gray-100 hover:text-gray-700 transition mr-1"
                              style={{ minHeight: 28, minWidth: 28 }}
                            >
                              {/* Use the local Icon component for 'copy' */}
                              <span className="text-base font-bold">
                                {/* eslint-disable-next-line */}
                                {React.createElement(
                                  require('../../common/components/Icon').default,
                                  { type: 'copy', style: { fontSize: 16 } },
                                )}
                              </span>
                            </ActionIcon>
                            <ActionIcon
                              color="red"
                              variant="subtle"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    'Are you sure you want to delete this override rule?',
                                  )
                                ) {
                                  setOverrides(prev => prev.filter((_, i) => i !== ovrIdx));
                                }
                              }}
                              title="Remove Override"
                              className="hover:bg-red-100 hover:text-red-700 transition"
                              style={{ minHeight: 28, minWidth: 28 }}
                            >
                              <span className="text-base font-bold">×</span>
                            </ActionIcon>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-gray-500 text-xs p-2">No overrides set.</div>
              )}
              {/* Sticky footer for Clear All and Save buttons */}
              <div
                className="flex flex-row justify-between gap-2 p-2 bg-white border-t sticky"
                style={{ bottom: 0, left: 0, right: 0, zIndex: 10 }}
              >
                <Button
                  color="red"
                  variant="outline"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to clear all overrides?')) {
                      setOverrides([]);
                    }
                  }}
                >
                  Clear All
                </Button>
                <div className="flex gap-2">
                  {hasUnsavedChanges && (
                    <Button
                      type="outline"
                      onClick={() => {
                        setOverrides(unsavedOverrides);
                        setHasUnsavedChanges(false);
                      }}
                    >
                      Revert Changes
                    </Button>
                  )}
                  <Button
                    type="primary"
                    disabled={!hasUnsavedChanges}
                    onClick={handleSaveOverrides}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </Modal>
        </div>
      }
    >
      {loading ? (
        <TextLoader text={loadingText} />
      ) : (
        <DataGrid
          rowData={rowData.map(row => {
            // Add classification to row data
            const rowWithClassification = {
              ...row,
              classification: row.projectId ? projectClassifications[row.projectId] || '' : ''
            };
            
            // Get user data for token processing
            const userData = getUserDataForRow(rowWithClassification, enrichedUserMap);
            
            // Find matching overrides (no token processing for conditions)
            const matchingOverrides = overrides.filter(ovr =>
              ovr.conditions.every(cond => String(rowWithClassification[cond.field]) === String(cond.value)),
            );
            
            if (matchingOverrides.length === 0) return rowWithClassification;
            
            // Process tokens in override values and merge
            const mergedOverride = matchingOverrides.reduce((acc, ovr) => {
              // Process tokens in override values
              const processedOverride = Object.entries(ovr.override).reduce((obj, [key, value]) => ({
                ...obj,
                [key]: replaceTokens(value, userData)
              }), {});
              
              return { ...acc, ...processedOverride };
            }, {});
            
            return { ...rowWithClassification, ...mergedOverride };
          })}
          onExportReady={onExportReady}
          exportFileName={`TimesheetSnapshot-WeekEnding(${endDate})-ExportedOn(${moment(
            new Date(),
          ).format('YYYYMMDD-hhmm')}).csv`}
        >
          <AgGridColumn field="endDate" width={125} />
          <AgGridColumn field="status" width={175} />
          <AgGridColumn field="lastName" />
          <AgGridColumn field="firstName" />
          <AgGridColumn field="payrollId" headerName="Payroll ID" />
          <AgGridColumn field="department" />
          <AgGridColumn field="email" />
          <AgGridColumn field="clientName" width={150} />
          <AgGridColumn field="project" />
          <AgGridColumn field="projectCode" />
          <AgGridColumn field="milestone" />
          <AgGridColumn field="deliverable" />
          <AgGridColumn field="taskdate" width={110} />
          <AgGridColumn field="hours" width={110} />
          <AgGridColumn field="role" />
          <AgGridColumn field="rate" />
          <AgGridColumn field="name" />
          <AgGridColumn field="totalCost" />
          <AgGridColumn field="supervisor" />
          <AgGridColumn field="approvers" />
          <AgGridColumn field="approvedOn" />
          <AgGridColumn field="comment" />
          <AgGridColumn field="classification" />
        </DataGrid>
      )}
    </Card>
  );
};

export default TimesheetDailySnapshotEnhancedReport;
